import { useState } from "react";
import { Copy, Check, Code2, Zap, Shield, Globe, ExternalLink } from "lucide-react";

const BASE = typeof window !== "undefined" ? window.location.origin : "";

const SECTIONS = [
    { id: "overview", label: "Overview" },
    { id: "auth", label: "Authentication" },
    { id: "create-payout", label: "Create a payout" },
    { id: "status", label: "Check status" },
    { id: "balance", label: "Wallet balance" },
    { id: "webhooks", label: "Webhooks" },
    { id: "errors", label: "Error codes" },
    { id: "testing", label: "Testing & limits" },
];

export default function PublicApiDocs() {
    const [copied, setCopied] = useState("");

    const copy = async (text, k) => {
        try { await navigator.clipboard.writeText(text); setCopied(k); setTimeout(() => setCopied(""), 1500); } catch {}
    };

    return (
        <div data-testid="public-api-docs" className="min-h-screen bg-slate-50 text-slate-900">
            {/* Top bar */}
            <header className="sticky top-0 z-20 bg-white border-b border-slate-200">
                <div className="max-w-6xl mx-auto px-5 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-600 to-blue-600 flex items-center justify-center text-white font-bold">P</div>
                        <div>
                            <div className="font-bold text-sm leading-tight">Payouts Portal</div>
                            <div className="text-[10px] text-slate-500">Developer Documentation</div>
                        </div>
                    </div>
                    <a href="/partner/login" className="text-sm text-indigo-600 hover:underline">Sign in →</a>
                </div>
            </header>

            <div className="max-w-6xl mx-auto px-5 py-10 grid lg:grid-cols-[220px_1fr] gap-10">
                {/* Side nav */}
                <aside className="hidden lg:block">
                    <nav className="sticky top-20 space-y-0.5">
                        {SECTIONS.map((s) => (
                            <a key={s.id} href={`#${s.id}`} data-testid={`docs-nav-${s.id}`}
                                className="block px-3 py-1.5 rounded text-sm text-slate-600 hover:bg-indigo-50 hover:text-indigo-700">
                                {s.label}
                            </a>
                        ))}
                    </nav>
                </aside>

                <main className="space-y-16 min-w-0">
                    {/* Overview */}
                    <section id="overview">
                        <div className="flex items-center gap-2 text-xs text-indigo-600 font-semibold uppercase tracking-wider mb-2"><Zap className="w-3.5 h-3.5" /> Payouts API</div>
                        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Send payouts to any bank account in India.</h1>
                        <p className="text-base text-slate-600 mt-3 max-w-2xl">
                            A single, clean REST API to disburse money via IMPS, NEFT or RTGS. Hosted on our infrastructure — you don't need to deal with multiple payout vendors, their IP whitelists, or their quirks.
                        </p>
                        <div className="mt-6 grid sm:grid-cols-3 gap-3">
                            <Pill icon={Globe} title="Base URL" value={`${BASE}/api/v1`} />
                            <Pill icon={Shield} title="Auth" value="X-API-Key header" />
                            <Pill icon={Code2} title="Format" value="JSON · REST" />
                        </div>
                    </section>

                    {/* Auth */}
                    <section id="auth">
                        <H2>Authentication</H2>
                        <P>Every request must include your API key in the <Code>X-API-Key</Code> header. Create and manage keys from <A href="/partner/panel/api">API & Webhooks</A> in your dashboard.</P>
                        <Snippet copyKey="auth" copied={copied} onCopy={copy} code={`X-API-Key: pg_sk_live_••••••••••••••••••••••••••••••••`} />
                        <Callout>
                            <strong>Keep keys secret.</strong> If a key leaks, revoke it immediately from the dashboard — all live requests with the compromised key start returning 401.
                        </Callout>
                    </section>

                    {/* Create payout */}
                    <section id="create-payout">
                        <H2>Create a payout</H2>
                        <Method verb="POST" path="/api/v1/payout" />
                        <P>Debits your prepaid wallet synchronously, dispatches the transfer, and returns our <Code>payout_id</Code>. If dispatch fails, the wallet is refunded in the same request — you never see orphan debits.</P>

                        <H3>Request body</H3>
                        <ParamTable rows={[
                            ["amount", "number", "yes", "Amount in INR (not paise). Min ₹1."],
                            ["beneficiary.name", "string", "yes", "Account holder's full name."],
                            ["beneficiary.account_number", "string", "yes", "Bank account number."],
                            ["beneficiary.ifsc", "string", "yes", "IFSC code (11 chars, uppercase)."],
                            ["beneficiary.bank_name", "string", "no", "Human-readable bank name."],
                            ["payment_type", "string", "no", "One of AUTO · IMPS · NEFT · RTGS. Default AUTO."],
                            ["client_reference_id", "string", "no", "Your idempotency key. Unique per partner."],
                            ["mid", "string", "no", "Specific MID to dispatch via. Defaults to your first active MID."],
                        ]} />

                        <H3>Example request</H3>
                        <Snippet copyKey="create-req" copied={copied} onCopy={copy} code={`curl -X POST ${BASE}/api/v1/payout \\
  -H "X-API-Key: pg_sk_live_••••" \\
  -H "Content-Type: application/json" \\
  -d '{
    "amount": 1000,
    "beneficiary": {
      "name": "John Doe",
      "account_number": "000123456789",
      "ifsc": "HDFC0001234",
      "bank_name": "HDFC Bank"
    },
    "payment_type": "AUTO",
    "client_reference_id": "order_12345"
  }'`} />
                        <H3>Example response</H3>
                        <Snippet copyKey="create-res" copied={copied} onCopy={copy} code={`{
  "payout_id": "po_7a3c91e8d2f0",
  "status": "PROCESSING",
  "amount": 1000,
  "currency": "INR",
  "beneficiary": {"name": "John Doe", "account_number": "000123456789", "ifsc": "HDFC0001234"},
  "client_reference_id": "order_12345",
  "created_at": "2026-02-14T12:34:56.789Z"
}`} />
                    </section>

                    {/* Status */}
                    <section id="status">
                        <H2>Check payout status</H2>
                        <Method verb="GET" path="/api/v1/payout/status/{payout_id}" />
                        <Method verb="GET" path="/api/v1/payout/by-ref/{client_reference_id}" />
                        <P>Both endpoints return the same shape. Use <Code>by-ref</Code> when you want to look up a payout using your own idempotency key.</P>
                        <Snippet copyKey="status" copied={copied} onCopy={copy} code={`curl -H "X-API-Key: pg_sk_live_••••" \\
  ${BASE}/api/v1/payout/status/po_7a3c91e8d2f0`} />
                        <H3>Status values</H3>
                        <ul className="list-disc pl-5 text-sm text-slate-700 space-y-1">
                            <li><Code>PROCESSING</Code> — Accepted; dispatched to the bank network.</li>
                            <li><Code>SUCCESS</Code> — Credited. <Code>utr</Code> is populated.</li>
                            <li><Code>FAILED</Code> — Rejected. <Code>failure_reason</Code> explains why. Wallet is refunded automatically.</li>
                            <li><Code>REVERSED</Code> — Credited then returned by the destination bank (invalid account). Wallet is refunded.</li>
                        </ul>
                    </section>

                    {/* Balance */}
                    <section id="balance">
                        <H2>Wallet balance</H2>
                        <Method verb="GET" path="/api/v1/balance" />
                        <Snippet copyKey="balance" copied={copied} onCopy={copy} code={`{
  "balance": 50000,
  "currency": "INR",
  "updated_at": "2026-02-14T12:34:56.789Z"
}`} />
                    </section>

                    {/* Webhooks */}
                    <section id="webhooks">
                        <H2>Webhooks</H2>
                        <P>Configure a URL under <A href="/partner/panel/api">API & Webhooks</A>. We POST a JSON payload on every status change (SUCCESS, FAILED, REVERSED). We retry with exponential backoff; you can also replay failed deliveries from the dashboard.</P>

                        <H3>Payload (paygateway format)</H3>
                        <Snippet copyKey="wh-payload" copied={copied} onCopy={copy} code={`{
  "event": "payout.success",
  "payout_id": "po_7a3c91e8d2f0",
  "status": "SUCCESS",
  "amount": 1000,
  "utr": "HDFC1234567890",
  "beneficiary": {"name": "John Doe", "account_number": "000123456789", "ifsc": "HDFC0001234"},
  "client_reference_id": "order_12345",
  "created_at": "2026-02-14T12:34:56.789Z"
}`} />
                        <H3>Signature verification</H3>
                        <P>We sign every webhook with <Code>X-PG-Signature: sha256=&lt;hex&gt;</Code> computed as:</P>
                        <Snippet copyKey="hmac" copied={copied} onCopy={copy} code={`import hmac, hashlib
sig = hmac.new(secret.encode(), raw_body, hashlib.sha256).hexdigest()
# Compare constant-time with the X-PG-Signature header value`} />
                        <Callout tone="amber"><strong>Always verify.</strong> Reject requests whose signature doesn't match the shared secret. Rotate the secret from the dashboard whenever you need.</Callout>
                    </section>

                    {/* Errors */}
                    <section id="errors">
                        <H2>Error codes</H2>
                        <ParamTable rows={[
                            ["400", "bad_request", "Validation failure (missing field, bad IFSC, etc.)"],
                            ["401", "unauthorized", "Missing or invalid X-API-Key."],
                            ["402", "insufficient_funds", "Wallet balance too low to debit."],
                            ["403", "forbidden", "IP not whitelisted OR MID not assigned OR vendor not allowed."],
                            ["409", "duplicate", "Same client_reference_id used before."],
                            ["429", "rate_limited", "Vendor daily limit reached. Retry later or use a different MID."],
                            ["502", "vendor_error", "Vendor temporarily unavailable. Wallet refunded automatically."],
                        ]} />
                    </section>

                    {/* Testing */}
                    <section id="testing">
                        <H2>Testing & limits</H2>
                        <ul className="list-disc pl-5 text-sm text-slate-700 space-y-1.5">
                            <li>Create API keys in Live mode only — we don't ship sandbox keys. Use small amounts (₹1) in production for integration testing.</li>
                            <li>Each MID has a daily volume cap and per-txn min/max — see the MIDs tab in your dashboard.</li>
                            <li>Payouts above ₹10,000 made from the UI dashboard require 2FA on the user's account.</li>
                            <li>IMPS: instant · NEFT: next window (every 30 mins) · RTGS: business hours only.</li>
                        </ul>
                    </section>

                    <footer className="pt-8 border-t border-slate-200 text-xs text-slate-500 flex items-center justify-between">
                        <div>© Payouts Portal · All rights reserved.</div>
                        <a href="/partner/login" className="text-indigo-600 hover:underline flex items-center gap-1">Sign in <ExternalLink className="w-3 h-3" /></a>
                    </footer>
                </main>
            </div>
        </div>
    );
}

function H2({ children }) { return <h2 className="text-2xl font-bold tracking-tight mb-3">{children}</h2>; }
function H3({ children }) { return <h3 className="text-base font-semibold mt-5 mb-2 text-slate-800">{children}</h3>; }
function P({ children }) { return <p className="text-sm text-slate-700 mb-3">{children}</p>; }
function Code({ children }) { return <code className="px-1.5 py-0.5 bg-slate-100 rounded text-[12px] font-mono">{children}</code>; }
function A({ href, children }) { return <a href={href} className="text-indigo-600 hover:underline">{children}</a>; }

function Pill({ icon: Icon, title, value }) {
    return (
        <div className="bg-white border border-slate-200 rounded-md px-3 py-2 flex items-center gap-2">
            <Icon className="w-4 h-4 text-indigo-600 shrink-0" />
            <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-wider text-slate-500">{title}</div>
                <div className="text-xs font-mono font-semibold truncate">{value}</div>
            </div>
        </div>
    );
}

function Method({ verb, path }) {
    const colour = verb === "GET" ? "bg-blue-100 text-blue-800" : "bg-emerald-100 text-emerald-800";
    return (
        <div className="my-3 flex items-center gap-2 bg-slate-900 text-white rounded-md px-3 py-2 text-sm font-mono">
            <span className={`px-2 py-0.5 rounded ${colour}`}>{verb}</span>
            {path}
        </div>
    );
}

function Snippet({ code, copyKey, copied, onCopy }) {
    return (
        <div className="relative my-3">
            <pre className="bg-slate-900 text-emerald-100 rounded-md p-4 text-[12px] overflow-x-auto font-mono whitespace-pre">{code}</pre>
            <button data-testid={`docs-copy-${copyKey}`} onClick={() => onCopy(code, copyKey)}
                    className="absolute top-2 right-2 p-1.5 bg-slate-700 hover:bg-slate-600 text-white rounded text-xs">
                {copied === copyKey ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
            </button>
        </div>
    );
}

function ParamTable({ rows }) {
    return (
        <div className="my-3 overflow-x-auto rounded-md border border-slate-200">
            <table className="w-full text-sm">
                <thead className="bg-slate-50">
                    <tr>
                        <th className="px-3 py-2 text-left text-[11px] uppercase tracking-wider text-slate-500">Field / Code</th>
                        <th className="px-3 py-2 text-left text-[11px] uppercase tracking-wider text-slate-500">Type / Name</th>
                        <th className="px-3 py-2 text-left text-[11px] uppercase tracking-wider text-slate-500">Required</th>
                        <th className="px-3 py-2 text-left text-[11px] uppercase tracking-wider text-slate-500">Description</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                    {rows.map((r, i) => (
                        <tr key={i}>
                            <td className="px-3 py-2 font-mono text-xs">{r[0]}</td>
                            <td className="px-3 py-2 text-xs">{r[1]}</td>
                            <td className="px-3 py-2 text-xs">{r[2]}</td>
                            <td className="px-3 py-2 text-xs text-slate-600">{r[3]}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function Callout({ children, tone }) {
    const t = tone === "amber" ? "bg-amber-50 border-amber-200 text-amber-900" : "bg-indigo-50 border-indigo-200 text-indigo-900";
    return <div className={`my-3 border rounded-md p-3 text-xs ${t}`}>{children}</div>;
}
